// Slice-10 TanStack Router tree.
//
// Code-based router (rather than file-based) keeps the slice-10 surface
// area tight + skips the @tanstack/router-plugin Vite generator that
// would add codegen drift. Slice 11 SPA pass switches to file-based
// routes once the screen list is locked.

import { RootRoute, Route, Outlet, Navigate, redirect } from "@tanstack/react-router";
import { AppShell } from "./AppShell";
import { LoginScreen } from "../screens/LoginScreen";
import { ProfileScreen } from "../screens/ProfileScreen";
import { UsersScreen } from "../screens/UsersScreen";
import { RolesScreen } from "../screens/RolesScreen";
import { PermissionsScreen } from "../screens/PermissionsScreen";
import { CodeSetIndexScreen } from "../screens/reference/CodeSetIndexScreen";
import { CodeIndexScreen } from "../screens/reference/CodeIndexScreen";
import { TerminologyEditorScreen } from "../screens/reference/TerminologyEditorScreen";
import { CustomerConfigAdminScreen } from "../screens/reference/CustomerConfigAdminScreen";
import { FeatureFlagAdminScreen } from "../screens/reference/FeatureFlagAdminScreen";
import { UnitTreeScreen } from "../screens/organization/UnitTreeScreen";
import { UnitTypeCatalogueScreen } from "../screens/organization/UnitTypeCatalogueScreen";
import { StationFacilityScreen } from "../screens/organization/StationFacilityScreen";
import { EmployeeListScreen } from "../screens/workforce/EmployeeListScreen";
import { EmployeeDetailScreen } from "../screens/workforce/EmployeeDetailScreen";
import { RotaListScreen } from "../screens/rota-config/RotaListScreen";
import { RotaDetailScreen } from "../screens/rota-config/RotaDetailScreen";
import { VehicleListScreen } from "../screens/crewing/VehicleListScreen";
import { VehicleDetailScreen } from "../screens/crewing/VehicleDetailScreen";
import { getAccessToken } from "../api/client";

const rootRoute = new RootRoute({
  component: () => <Outlet />,
});

const loginRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginScreen,
});

const authedRoute = new Route({
  getParentRoute: () => rootRoute,
  id: "authed",
  beforeLoad: () => {
    // Guard: if no access token, send the user to /login. The
    // AuthProvider boot-time refresh will repopulate the token on a
    // hard reload before this loader runs (microtask ordering).
    if (!getAccessToken()) {
      throw redirect({ to: "/login" });
    }
    return undefined;
  },
  component: AppShell,
});

const indexRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/",
  component: () => <Navigate to="/me" replace />,
});

const meRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/me",
  component: ProfileScreen,
});

const usersRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/users",
  component: UsersScreen,
});

const rolesRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/roles",
  component: RolesScreen,
});

const permissionsRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/permissions",
  component: PermissionsScreen,
});

// ---- Module 2 / Slice 9 reference admin routes ---------------------------

const codeSetIndexRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/reference/code-sets",
  component: CodeSetIndexScreen,
});

const codeIndexRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/reference/code-sets/$codeSetId/codes",
  component: CodeIndexScreen,
});

const terminologyRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/reference/terminology",
  component: TerminologyEditorScreen,
});

const customerConfigRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/reference/customer-config",
  component: CustomerConfigAdminScreen,
});

const featureFlagsRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/feature-flags",
  component: FeatureFlagAdminScreen,
});

// ---- Module 3 / Slice 8 organization admin routes -----------------------
// Gated by the `organization.admin_ui.enabled` feature flag at runtime
// (the AppShell nav hides the entries when the flag is off; the route
// itself remains reachable for power users who already know the URL).

const organizationUnitsRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/organization/units",
  component: UnitTreeScreen,
});

const organizationUnitTypesRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/organization/unit-types",
  component: UnitTypeCatalogueScreen,
});

const organizationStationFacilityRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/organization/units/$unitId/station-facility",
  component: StationFacilityScreen,
});

// ---- Module 4 / Slice 5 workforce admin routes ----------------------------
// Gated by the `workforce.admin_ui.enabled` feature flag at the AppShell
// nav level (the route itself remains reachable for power users who
// already know the URL). PATTERNS.md §2.1.

const workforceEmployeeListRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/workforce/employees",
  component: EmployeeListScreen,
});

const workforceEmployeeDetailRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/workforce/employees/$employeeId",
  component: EmployeeDetailScreen,
});

// ---- Module 5 / Slice 5 rota-config admin routes -------------------------
// Gated by the `rota.admin_ui.enabled` feature flag at the AppShell nav
// level (the route itself remains reachable for power users). PATTERNS.md
// §2.1 + MODULE_5_PLAN.md §10.

const rotaConfigListRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/rota-config/rotas",
  component: RotaListScreen,
});

const rotaConfigDetailRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/rota-config/rotas/$rotaId",
  component: RotaDetailScreen,
});

// ---- Module 6 / Slice 5 crewing admin routes -----------------------------
// Gated by the `crewing.admin_ui.enabled` feature flag at the AppShell
// nav level (the route itself remains reachable for power users).
// PATTERNS.md §2.1 + MODULE_6_PLAN.md §10.

const crewingVehicleListRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/crewing/vehicles",
  component: VehicleListScreen,
});

const crewingVehicleDetailRoute = new Route({
  getParentRoute: () => authedRoute,
  path: "/admin/crewing/vehicles/$vehicleId",
  component: VehicleDetailScreen,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  authedRoute.addChildren([
    indexRoute, meRoute, usersRoute, rolesRoute, permissionsRoute,
    // Module 2 / Slice 9.
    codeSetIndexRoute, codeIndexRoute, terminologyRoute,
    customerConfigRoute, featureFlagsRoute,
    // Module 3 / Slice 8.
    organizationUnitsRoute, organizationUnitTypesRoute, organizationStationFacilityRoute,
    // Module 4 / Slice 5.
    workforceEmployeeListRoute, workforceEmployeeDetailRoute,
    // Module 5 / Slice 5.
    rotaConfigListRoute, rotaConfigDetailRoute,
    // Module 6 / Slice 5.
    crewingVehicleListRoute, crewingVehicleDetailRoute,
  ]),
]);
